import React from "react";

import {
  IgButton,
  IgPaper,
  IgRadio,
  IgTypography,
} from "@intelligems/ig-theme";
import { AnalyticsProductDto, AnalyticsViewType } from "@intelligems/ig-types";
import { FormControlLabel, RadioGroup } from "@mui/material";
import Grid from "@mui/material/Grid";
import { SelectedProductsPreview } from "@src/features/personalizations/components/offerModifications/discount/offerProductPickerDialog/SelectedProductsPreview";
import { useDispatch, useSelector } from "@src/store";
import { resetAnalyticsProducts } from "@src/store/slices/analyticsProducts";
import { getHasAnyUpsellBlocks } from "@src/store/slices/checkoutBlocks/selectors";
import {
  selectedExperience,
  setExperienceAnalyticsViewType,
} from "@src/store/slices/experiences";
import { closeProductPicker } from "@src/store/slices/productPicker";
import { openAnalyticsProductPicker } from "@src/store/slices/productPicker/thunks/analytics/openAnalyticsProductPicker";
import { theme } from "@src/theme";

interface SetAnalyticsViewTypeProps {
  onDelete: (productId: string) => void;
  analyticsProducts: AnalyticsProductDto[];
  isReadOnly?: boolean;
}

export const SetAnalyticsViewType = ({
  onDelete,
  analyticsProducts,
  isReadOnly = false,
}: SetAnalyticsViewTypeProps) => {
  const experience = useSelector(selectedExperience);
  const hasUpsellBlocks = useSelector(getHasAnyUpsellBlocks());
  const dispatch = useDispatch();
  // `experience.type` is typed as a non-null enum but can be null/undefined at
  // runtime for a partially-hydrated experience. Guard with optional chaining,
  // mirroring the existing pattern in getProductType/ExperimentProvider.
  const isShippingOrContent =
    experience.type === "shipping" || !!experience.type?.includes("content");

  if (experience.type === "pricing" || isShippingOrContent || hasUpsellBlocks) {
    return (
      <Grid container direction={"column"}>
        <IgPaper>
          <Grid>
            <IgTypography variant={"h2"} sx={{ fontWeight: 600 }}>
              How would you like to measure conversions when viewing results?
            </IgTypography>
          </Grid>
          <Grid mb={1}>
            <IgTypography sx={{ color: theme.palette.text.secondary }}>
              You can always change this later.
            </IgTypography>
          </Grid>
          <RadioGroup
            aria-labelledby="demo-row-radio-buttons-group-label"
            name="row-radio-buttons-group"
            onChange={
              isReadOnly
                ? undefined
                : (e) => {
                    dispatch(
                      setExperienceAnalyticsViewType(
                        e.target.value as AnalyticsViewType
                      )
                    );
                    if (
                      isShippingOrContent &&
                      e.target.value === "test_orders_only"
                    ) {
                      dispatch(resetAnalyticsProducts());
                      void dispatch(openAnalyticsProductPicker());
                    } else if (
                      isShippingOrContent &&
                      e.target.value === "sitewide_orders"
                    ) {
                      dispatch(resetAnalyticsProducts());
                      dispatch(closeProductPicker());
                    }
                  }
            }
            value={experience.experienceAnalysis.analyticsViewType}
          >
            <FormControlLabel
              value={"none"}
              sx={{ visibility: "hidden", display: "none" }}
              control={<IgRadio size={"small"} />}
              label={""}
            />
            <FormControlLabel
              value={"sitewide_orders"}
              control={<IgRadio size={"small"} />}
              label={
                <IgTypography>
                  {experience.type === "pricing" ||
                  isShippingOrContent ||
                  hasUpsellBlocks
                    ? "Count all orders, regardless of products in the order"
                    : "Count all orders, regardless of products included in an order"}
                </IgTypography>
              }
            />
            <FormControlLabel
              value={"test_orders_only"}
              control={<IgRadio size={"small"} />}
              label={
                <Grid
                  container
                  direction={"row"}
                  alignItems={"center"}
                  spacing={2}
                >
                  <Grid>
                    <IgTypography>
                      {experience.type === "pricing"
                        ? "Count only orders containing tested products"
                        : hasUpsellBlocks
                          ? "Count only orders containing upsell products"
                          : "Count only orders containing certain products"}
                    </IgTypography>
                  </Grid>
                  {!isReadOnly &&
                    isShippingOrContent &&
                    experience.experienceAnalysis.analyticsViewType ===
                      "test_orders_only" && (
                      <Grid>
                        <IgButton
                          onClick={() =>
                            void dispatch(openAnalyticsProductPicker())
                          }
                        >
                          Edit Products
                        </IgButton>
                      </Grid>
                    )}
                </Grid>
              }
            />
          </RadioGroup>
          <SelectedProductsPreview
            isView={isReadOnly}
            products={analyticsProducts}
            onDelete={onDelete}
          />
        </IgPaper>
      </Grid>
    );
  }
  return null;
};
